import { deflate, inflate } from "pako";
import moment from "moment";

// Web-based package share links.
//
// Instead of generating a Branch short link we extract a small, FLAT object from
// the shared package (selectedHotelToShare) — only the fields needed to run the
// API search and to display the package card — serialize it to JSON, compress it
// with pako (deflate) and base64url-encode the result so it can travel safely
// inside a query parameter. Keeping the data flat and minimal keeps the link
// short. The receiving page reverses the process to get the flat object back.

// --- base64url helpers (URL-safe, no padding) ---
const toBase64Url = (bytes) => {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};

const fromBase64Url = (str) => {
    let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
        base64 += "=";
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

/**
 * Compress a string into a URL-safe token.
 * @param {string} value e.g. a JSON string
 * @returns {string} compressed, base64url-encoded token
 */
export const encodeShareParams = (value) => {
    // level 9 = maximum compression, to keep the resulting link as short as possible.
    const compressed = deflate(value, { level: 9 });
    return toBase64Url(compressed);
};

/**
 * Reverse of encodeShareParams: restore the original string.
 * @param {string} token compressed, base64url-encoded token
 * @returns {string} original string
 */
export const decodeShareParams = (token) => {
    const bytes = fromBase64Url(token);
    const decoded = new TextDecoder().decode(inflate(bytes));

    try {
        const parsed = JSON.parse(decoded);
        return JSON.stringify(expandShareData(parsed));
    } catch (e) {
        return decoded;
    }
};

// Central mapping from full share-data keys to short payload keys.
const SHARE_KEY_ALIASES = {
    hotelId: "hI",
    name: "n",
    image: "i",
    starRating: "sR",
    guestRatingReviewText: "gR",
    address: "a",
    perPersonPrice: "pP",
    currency: "c",
    departure: "d",
    destination: "ds",
    departureDate: "dD",
    returnDate: "rD",
    airlineCode: "aC",
    rooms: "rm",
    adults: "ad",
    children: "ch",
    infants: "if",
    flightNumbersString: "fN",
};

const SHORT_TO_SHARE_KEY = Object.fromEntries(
    Object.entries(SHARE_KEY_ALIASES).map(([original, alias]) => [alias, original])
);

const compactShareData = (data) =>
    Object.entries(data).reduce((result, [key, value]) => {
        result[SHARE_KEY_ALIASES[key] || key] = value;
        return result;
    }, {});

const expandShareData = (data) =>
    Object.entries(data).reduce((result, [key, value]) => {
        result[SHORT_TO_SHARE_KEY[key] || key] = value;
        return result;
    }, {});

// Query-string key under which the compressed token travels in the share link.
export const SHARE_PARAM_KEY = "data";

// Parse the room / passenger counts the package carries inside the webDetails
// href (e.g. ...&NumRoom=1&NumAdult=R1:2&NumChild=R1:0). Values look like
// "R1:2|R2:1"; we sum the number after each ":".
const sumPaxParam = (value) =>
    (value || "")
        .split("|")
        .reduce((total, part) => total + (Number(part.split(":")[1]) || 0), 0);

const parsePaxFromHref = (href) => {
    // The source href carries no infant count, so infants stays 0.
    const result = { rooms: 1, adults: 0, children: 0, infants: 0 };
    if (!href) {
        return result;
    }
    try {
        const params = new URL(href).searchParams;
        result.rooms = Number(params.get("NumRoom")) || 1;
        result.adults = sumPaxParam(params.get("NumAdult"));
        result.children = sumPaxParam(params.get("NumChild"));
    } catch (e) {
        // keep defaults
    }
    return result;
};

// Date part ("2026-08-28") from a flight leg's ISO datetime.
const toDate = (dateTime) => (dateTime ? String(dateTime).split("T")[0] : undefined);

/**
 * Flight numbers of a flight offer, in itinerary order, pipe-separated
 * (e.g. "AA123|AA456|AA789"). Shared by the web link and the Branch link so both
 * pin the same itinerary; mirrors the mobile app's helpers/branchio.js.
 *
 * @param {object} flightOffer
 * @returns {string|undefined}
 */
export const getFlightNumbersString = (flightOffer) =>
    flightOffer?.segmentIds
        ?.map((segmentId) =>
            flightOffer?.segments?.find((segment) => segment.segmentId === segmentId)
        )
        ?.flatMap((segment) => segment?.legs ?? [])
        ?.map((leg) => leg?.flightNumber)
        ?.filter(Boolean)
        ?.join("|");

/**
 * Extract a small, FLAT object from the shared package containing only what is
 * needed to (a) run the API search and (b) display the package card. No nesting,
 * so the compressed link stays as short as possible.
 *
 * @param {object} selectedHotelToShare the package object from redux (DetailsTopBar)
 * @returns {object|null} the flat share data
 */
export const buildShareData = (selectedHotelToShare, flight) => {
    if (!selectedHotelToShare) {
        return null;
    }

    const hotel = selectedHotelToShare;
    const flightOffer = flight;

    // departure ticket data
    const departureSegmentId = flightOffer?.segmentIds?.[0];
    const departureFlightSegment = flightOffer?.segments?.find(
        (segment) => segment.segmentId === departureSegmentId
    );
    const outboundLeg = departureFlightSegment?.legs?.[0];

    // arrival (return) ticket data
    const arrivalSegmentId =
        flightOffer?.segmentIds?.[flightOffer?.segmentIds?.length - 1];
    const arrivalFlightSegment = flightOffer?.segments?.find(
        (segment) => segment.segmentId === arrivalSegmentId
    );
    const returnLeg = arrivalFlightSegment?.legs?.[0];

    const flightNumbersString = getFlightNumbersString(flightOffer);

    const pax = parsePaxFromHref(hotel?.roomTypes?.[0]?.links?.webDetails?.href);

    return compactShareData({
        // --- hotel / display ---
        hotelId: hotel?.id,
        name: hotel?.name,
        image: hotel?.thumbnailUrl || hotel?.media?.[0]?.url,
        starRating: hotel?.starRating,
        // combined rating + review count, e.g. "8.8 (150)"
        guestRatingReviewText: `${hotel?.guestRating || 0} (${hotel?.guestReviewCount || 0})`,
        // combined city + country, e.g. "Barcelona, Spain"
        address: [hotel?.location?.address?.city, hotel?.location?.address?.country]
            .filter(Boolean)
            .join(", "),
        // --- price ---
        // value only; the currency lives in its own property
        perPersonPrice: hotel?.perPersonPrice?.totalPrice,
        currency: hotel?.price?.currency || hotel?.perPersonPrice?.currency,
        // --- flight / search inputs ---
        departure: outboundLeg?.departureAirport?.code,
        destination: returnLeg?.departureAirport?.code,
        departureDate: toDate(outboundLeg?.departureDateTime),
        returnDate: toDate(returnLeg?.departureDateTime),
        airlineCode: outboundLeg?.marketingAirlineCode,
        // --- passengers / rooms ---
        rooms: pax.rooms,
        adults: pax.adults,
        children: pax.children,
        infants: pax.infants,
        flightNumbersString,
    });
};

/**
 * Build a web-based package share link from the shared package object.
 * Embeds the flat share data as a compressed token in the format:
 *   https://{current-host}/link?data={token}
 * The host is captured dynamically from window.location (never hardcoded).
 *
 * @param {object} selectedHotelToShare the package object from redux (DetailsTopBar)
 * @returns {string} the share link
 */
export const buildShareLink = (selectedHotelToShare, flight) => {
    const data = buildShareData(selectedHotelToShare, flight);
    const token = encodeShareParams(JSON.stringify(data));
    return `${window.location.origin}/link?${SHARE_PARAM_KEY}=${token}`;
};

/**
 * Decode a share-link token back into the flat share-data object.
 * @param {string} token compressed, base64url-encoded token
 * @returns {object|null} the flat share data, or null if it cannot be decoded
 */
export const decodeShareData = (token) => {
    if (!token) {
        return null;
    }
    try {
        return JSON.parse(decodeShareParams(token));
    } catch (e) {
        console.error("Failed to decode shared package link:", e);
        return null;
    }
};

/**
 * Build the listing filter chips (date range / travelers-rooms / nights) for the
 * Web Link card from the FLAT share data. Mirrors the chips produced by
 * getListingItemFilters (src/helpers/common.js) for the nested Branch offer.
 *
 * @param {object} data the flat share data
 * @param {(key: string) => string} T translation function
 * @returns {Array<{id: number, title: string}>} filter chips
 */
export const getWebLinkFilters = (data, T) => {
    if (!data) {
        return [];
    }
    const checkInDate = data.departureDate;
    const checkOutDate = data.returnDate;

    const total =
        (Number(data.adults) || 0) + (Number(data.children) || 0) + (Number(data.infants) || 0);
    const rooms = Number(data.rooms) || 1;
    const nights = moment(checkOutDate).diff(moment(checkInDate), "days");

    return [
        {
            id: 1,
            title: `${moment.parseZone(checkInDate).format("MMM DD")}-${moment
                .parseZone(checkOutDate)
                .format("DD")}`,
        },
        {
            id: 2,
            title: `${total} ${T("Home:Home_List_View_Pers")} / ${rooms} ${T("Home:Home_List_View_Rm")}`,
        },
        {
            id: 3,
            title: `${nights} ${nights > 1 ? T("Common:Nights") : T("Common:Night")}`,
        },
    ];
};
